
(function () {
  'use strict';

  var STORAGE_KEY = 'sammmm:recent-products';
  var MAX_ITEMS = 10; // The backend's own cap. Mirrored so both paths behave alike.
  // Last-resort only: the real base comes from Theme settings > SammmmFam API,
  // rendered into data-api-base by the section and the PDP tracker.
  var DEFAULT_API_BASE = 'https://fam.sammmm.com';
  var CARD_SECTION_ID = 'recent-product-card';

  function customerId(el) {
    var native = ((el && el.dataset.customerId) || '').trim();
    if (native) return native;
    try {
      return (window.localStorage.getItem('kp_customer_id') || '').trim();
    } catch (error) {
      return '';
    }
  }

  function apiBase(el) {
    var base = ((el && el.dataset.apiBase) || '').trim();
    return base.replace(/\/$/, '') || DEFAULT_API_BASE;
  }

  function endpoint(base, id) {
    return base + '/api/sammmmfam/recent-products?customer_id=' + encodeURIComponent(id);
  }

  function apiHeaders(base, extra) {
    var headers = {};
    for (var key in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, key)) headers[key] = extra[key];
    }
    if (base && base.indexOf('ngrok') !== -1) headers['ngrok-skip-browser-warning'] = '1';
    return headers;
  }

  /* ── localStorage path (signed-out shoppers) ─────────────────────────── */

  function readLocal() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      // Private mode / quota / corrupt JSON — an empty history is the right
      // answer for all of them.
      return [];
    }
  }

  function writeLocal(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Nothing to do and nothing worth telling the shopper.
    }
  }

  /**
   * Apply the server's own two rules locally: re-viewing moves an entry to the
   * front rather than duplicating it, and the 11th save drops the oldest.
   */
  function pushLocal(entry) {
    if (!entry || !entry.handle) return;
    var next = readLocal().filter(function (item) {
      return item && item.handle !== entry.handle;
    });
    next.unshift({ product_id: String(entry.product_id || ''), handle: entry.handle });
    writeLocal(next.slice(0, MAX_ITEMS));
  }

  function record(options) {
    var opts = options || {};
    var id = opts.customerId || customerId();
    var handle = opts.handle;
    var productId = opts.productId;

    if (!productId) return Promise.resolve(false);

    if (!id) {
      pushLocal({ product_id: productId, handle: handle });
      return Promise.resolve(true);
    }

    var base = (opts.apiBase || '').trim().replace(/\/$/, '') || DEFAULT_API_BASE;

    return fetch(endpoint(base, id), {
      method: 'POST',
      // A CORS "simple" content type, so production skips the preflight.
      headers: apiHeaders(base, { 'Content-Type': 'application/x-www-form-urlencoded' }),
      body: new URLSearchParams({ product_id: String(productId), handle: handle || '' })
    })
      .then(function () {
        return true;
      })
      .catch(function () {
        return false;
      });
  }


  function list(el) {
    var id = customerId(el);
    if (!id) return Promise.resolve(readLocal());

    var base = apiBase(el);

    return fetch(endpoint(base, id), { headers: apiHeaders(base, { Accept: 'application/json' }) })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (data) {
        // A signed-out write returns 200 having stored nothing, so trust
        // `success` in the body rather than the status alone.
        if (!data || !data.success || !Array.isArray(data.products)) return [];
        return data.products;
      })
      .catch(function () {
        return [];
      });
  }

  /* ── Card hydration ──────────────────────────────────────────────────── */

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '');
  }

  function fetchCard(handle) {
    var url = rootUrl() + '/products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION_ID;
    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-recent-card]');
      })
      .catch(function () {
        return null;
      });
  }


  function initSliderNav(root) {
    var slider = root.querySelector('[data-recent-track]');
    if (!slider) return;

    var prevBtn = root.querySelector('[data-ps-prev]');
    var nextBtn = root.querySelector('[data-ps-next]');
    var progress = root.querySelector('[data-ps-progress]');

    function update() {
      var maxScrollLeft = slider.scrollWidth - slider.clientWidth;
      if (prevBtn) prevBtn.style.opacity = slider.scrollLeft > 0 ? '1' : '0.5';
      if (nextBtn) nextBtn.style.opacity = slider.scrollLeft < maxScrollLeft - 1 ? '1' : '0.5';
      if (progress) {
        progress.style.width = (maxScrollLeft > 0 ? (slider.scrollLeft / maxScrollLeft) * 100 : 0) + '%';
      }
    }

    // The arrows are <svg role="button" tabindex="0">, so a click handler alone
    // would leave them reachable by keyboard but impossible to activate.
    function bindArrow(button, distance) {
      if (!button) return;
      function scroll() {
        slider.scrollBy({ left: distance, behavior: 'smooth' });
      }
      button.addEventListener('click', scroll);
      button.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        scroll();
      });
    }

    bindArrow(nextBtn, 300);
    bindArrow(prevBtn, -300);

    slider.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    update();
  }

  function reveal(el) {
    el.hidden = false;
    var wrapper = el.closest('.shopify-section');
    if (wrapper) wrapper.hidden = false;
  }

  function revealEmptyInDesignMode(el) {
    // Shoppers get nothing; merchants in the theme editor get an explanation,
    // otherwise the section looks broken rather than simply empty.
    if (!el.dataset.designMode) return;
    var note = el.querySelector('[data-recent-empty-note]');
    if (note) note.hidden = false;
    reveal(el);
  }

  function hydrate(el) {
    if (el.dataset.recentHydrated) return;
    el.dataset.recentHydrated = 'true';

    var wrapper = el.closest('.shopify-section');
    if (wrapper) wrapper.hidden = true;

    var track = el.querySelector('[data-recent-track]');
    if (!track) return;

    var exclude = el.dataset.excludeHandle || '';
    var max = parseInt(el.dataset.maxProducts, 10) || 8;

    list(el)
      .then(function (products) {
        var handles = products
          .map(function (item) {
            return item && item.handle;
          })
          // `handle` is null when the view was recorded without one, and the
          // product currently on screen is dropped so the rail never offers the
          // page the shopper is already reading.
          .filter(function (handle) {
            return handle && handle !== exclude;
          });

        if (!handles.length) {
          revealEmptyInDesignMode(el);
          return;
        }

        // In parallel, but re-assembled in the list's order below so the rail
        // stays newest-first regardless of which response landed when.
        return Promise.all(handles.map(fetchCard)).then(function (cards) {
          // No-search / no-index products come back without a card, so cap
          // after dropping them to keep the rail full.
          var found = cards.filter(Boolean).slice(0, max);
          if (!found.length) {
            revealEmptyInDesignMode(el);
            return;
          }

          var fragment = document.createDocumentFragment();
          found.forEach(function (card) {
            fragment.appendChild(card);
          });
          track.appendChild(fragment);

          reveal(el);
          initSliderNav(el);
        });
      })
      .catch(function () {
        revealEmptyInDesignMode(el);
      });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-recent-products]').forEach(hydrate);
  }

  window.RecentProducts = {
    STORAGE_KEY: STORAGE_KEY,
    MAX_ITEMS: MAX_ITEMS,
    customerId: customerId,
    readLocal: readLocal,
    pushLocal: pushLocal,
    record: record,
    list: list,
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: a re-rendered section arrives with a fresh, empty track.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
